import React from 'react';

import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Button, Divider } from '@mui/material';

import { observer } from 'mobx-react';
import { enqueueSnackbar } from 'notistack';
import classes from './CheckoutForm.module.scss';
import { PaymentButton } from '../../../../components/checkout/PaymentButton/PaymentButton';
import { paymentElementsOptions } from '../../../../constants/paymentElementsOptions';
import { getPriceView } from '../../../../../shared/utils/stringUtils';
import { challengeCheckoutStore } from '../../../../stores/challengeCheckoutStore';
import { apiStatusStore } from '../../../../../shared/stores/apiStatusStore';
import { SOLUTION_TYPE } from '../../../../../shared/constants/user-info/solutionType';
import { generatePaymentResultUrl } from '../../../../utils/generatePaymentResultUrl/generatePaymentResultUrl';
import { getRouteParam } from '../../../../../shared/utils/angularUtils';
import {
  checkoutQueryParams,
  paymentResultQueryParams,
} from '../../../../constants/pathQueryParams';
import { TermsLinks } from '../../../../../shared/components/TermsLinks/TermsLinks';
import { CHALLENGE_PAYMENT_RESULT_TYPE } from '../../../../constants/challengePaymentResultType';

const CheckoutForm = observer(() => {
  const stripe = useStripe();
  const elements = useElements();

  const retryPath = getRouteParam(checkoutQueryParams.retryPath);
  const participantId = getRouteParam(checkoutQueryParams.participantId);

  const { challengeCheckoutInfo } = challengeCheckoutStore;
  const { loadingStart, loadingEnd } = apiStatusStore;

  if (!challengeCheckoutInfo) {
    return null;
  }

  const { checkoutDetails, paymentIntent, challenge } = challengeCheckoutInfo;
  const returnUrl = generatePaymentResultUrl(SOLUTION_TYPE.CHALLENGE, {
    [paymentResultQueryParams.retryPath]: retryPath,
    [paymentResultQueryParams.connectedAccountId]: paymentIntent.connectedAccountId,
    [paymentResultQueryParams.paymentIntentId]: paymentIntent.paymentIntentId || '',
    [paymentResultQueryParams.participantId]: participantId,
    [paymentResultQueryParams.paymentResultType]:
      CHALLENGE_PAYMENT_RESULT_TYPE.WITH_PAYMENT,
  });

  const paymentButtonProps = {
    returnUrl,
    label: challenge.challengeName,
    totalAmount: checkoutDetails.totalAmount,
    clientSecret: paymentIntent.clientSecret,
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    loadingStart(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: returnUrl,
      },
    });

    if (error) {
      enqueueSnackbar(error.message || 'Something went wrong. Please try again later', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }

    loadingEnd(true);
  };

  return (
    <form className={classes.form} onSubmit={handleSubmit}>
      <PaymentButton type="googlePay" {...paymentButtonProps} />
      <PaymentButton
        type="applePay"
        {...paymentButtonProps}
        className={classes.applePayButton}
      />
      <Divider className={classes.divider} flexItem textAlign="center">
        Or pay with card
      </Divider>
      <PaymentElement id="card-info-box" options={paymentElementsOptions} />
      <Button variant="contained" type="submit" fullWidth>
        Pay {getPriceView(checkoutDetails.totalAmount)}
      </Button>
      <TermsLinks />
    </form>
  );
});

export { CheckoutForm };
